import React, { useMemo, useRef, useState, useEffect } from 'react'
import GlassPanel from '../UI/GlassPanel'
import { clickableProps } from '../../utils/clickableProps'
import { buildTimelineModel } from '../../utils/timelineUtils'

const formatDate = (value) => {
  const date = new Date(value)
  if (isNaN(date.getTime())) return ''
  return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })
}

/**
 * One horizontal timeline across a passport: past behind, upcoming ahead, a
 * "now" marker between them.
 *
 * Positions are computed on a real time axis rather than by index, so a gap of
 * five years reads as a gap. Events are laid out in lanes so overlapping dates
 * stay individually clickable instead of stacking into one dot.
 */
const EventTimeline = ({ events = [], onEventClick }) => {
  const scrollRef = useRef(null)
  const [drag, setDrag] = useState(null)
  const [hovered, setHovered] = useState(null)

  const model = useMemo(() => buildTimelineModel(events), [events])

  // Open on "now" rather than at the far left — the interesting part of a
  // passport is the boundary between what you have done and what is next.
  useEffect(() => {
    const el = scrollRef.current
    if (!el || !model) return
    const target = (model.nowLeft / 100) * el.scrollWidth - el.clientWidth / 2
    el.scrollLeft = Math.max(0, target)
  }, [model])

  if (!model) {
    return (
      <GlassPanel>
        <h2 className="text-xl font-bold text-white mb-2">🗓 Timeline</h2>
        <p className="text-gray-400 text-sm">
          Add events with dates and they'll appear here on a scrubbable timeline.
        </p>
      </GlassPanel>
    )
  }

  const startDrag = (clientX) => {
    const el = scrollRef.current
    setDrag({ x: clientX, scrollLeft: el.scrollLeft })
  }

  const moveDrag = (clientX) => {
    if (!drag) return
    scrollRef.current.scrollLeft = drag.scrollLeft - (clientX - drag.x)
  }

  const width = Math.max(100, model.placed.length * 14)
  const height = model.laneCount * 84 + 48

  return (
    <GlassPanel>
      <div className="flex items-baseline justify-between gap-3 flex-wrap mb-1">
        <h2 className="text-xl font-bold text-white">🗓 Timeline</h2>
        <p className="text-sm text-gray-400">Drag to scrub · click an event to open it</p>
      </div>

      <div
        ref={scrollRef}
        className={`relative overflow-x-auto overflow-y-hidden pb-2 ${drag ? 'cursor-grabbing select-none' : 'cursor-grab'}`}
        onMouseDown={(e) => startDrag(e.clientX)}
        onMouseMove={(e) => moveDrag(e.clientX)}
        onMouseUp={() => setDrag(null)}
        onMouseLeave={() => setDrag(null)}
        onTouchStart={(e) => startDrag(e.touches[0].clientX)}
        onTouchMove={(e) => moveDrag(e.touches[0].clientX)}
        onTouchEnd={() => setDrag(null)}
      >
        <div className="relative" style={{ width: `${width}%`, height: `${height}px` }}>
          {/* Year ticks */}
          {model.years.map(tick => (
            <div
              key={tick.year}
              className="absolute top-0 bottom-0 border-l border-white/10"
              style={{ left: `${tick.left}%` }}
            >
              <span className="absolute top-0 left-1 text-xs text-gray-500 tabular-nums">
                {tick.year}
              </span>
            </div>
          ))}

          {/* Now marker */}
          <div
            className="absolute top-0 bottom-0 border-l-2 border-pink-500/70 z-10"
            style={{ left: `${model.nowLeft}%` }}
          >
            <span className="absolute -top-0.5 left-1 text-[10px] font-semibold text-pink-300 whitespace-nowrap">
              now
            </span>
          </div>

          {/* Baseline */}
          <div className="absolute left-0 right-0 border-t border-white/15" style={{ top: '28px' }} />

          {/* Events */}
          {model.placed.map(event => (
            <div
              key={event.id}
              className="absolute"
              style={{ left: `${event.left}%`, top: `${40 + event.lane * 84}px` }}
              onMouseEnter={() => setHovered(event.id)}
              onMouseLeave={() => setHovered(null)}
            >
              <div
                {...clickableProps(
                  () => {
                    // A drag that ends over a card should scrub, not navigate.
                    if (!drag) onEventClick(event.id)
                  },
                  `${event.name}, ${formatDate(event.start)}`
                )}
                className={`w-40 rounded-xl border px-3 py-2 transition-all duration-200 focus:outline-none focus-visible:ring-2 focus-visible:ring-purple-400 ${
                  event.isPast
                    ? 'bg-white/5 border-white/15 hover:bg-white/10'
                    : 'bg-purple-500/20 border-purple-400/50 hover:bg-purple-500/30'
                } ${hovered === event.id ? 'scale-105 z-20' : ''}`}
              >
                <div className="text-sm font-semibold text-white truncate">{event.name}</div>
                <div className="text-xs text-gray-400 truncate">{formatDate(event.start)}</div>
                {event.venueName && (
                  <div className="text-xs text-gray-500 truncate">{event.venueName}</div>
                )}
              </div>

              {/* Stem down to the axis */}
              <div
                className={`absolute left-4 -top-3 w-px h-3 ${
                  event.isPast ? 'bg-white/25' : 'bg-purple-400/60'
                }`}
              />
              <div
                className={`absolute left-[13px] -top-[18px] w-2 h-2 rounded-full ${
                  event.isPast ? 'bg-white/40' : 'bg-purple-400'
                }`}
              />
            </div>
          ))}
        </div>
      </div>
    </GlassPanel>
  )
}

export default EventTimeline
